import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import { useInView } from 'react-intersection-observer'
import getSitesForStaticPaths from 'helpers/getSitesForStaticPaths'
import toTitleCase from 'helpers/toTitleCase'
import INDEX_QUERY from 'graphql/index.query'

import Loading from 'components/Loading'
import SplashText from 'components/SplashText'
import WordpressSEO from 'components/WordpressSEO'
import Layout from 'components/Layout'
import OurWorldBlock from 'components/BlocksHomepage/OurWorldBlock'
import SliderCover from 'components/SliderCover/dynamic'

import s from './index.module.css'
import { LocalBusinessJsonLd } from 'next-seo'
import StateInitializer from '../../components/StateInitializer'
import getGlobalSettings from '../../helpers/getGlobalSettings'
import getSites from '../../helpers/getSites'
import InstagramBlock from '../../components/BlocksHomepage/InstagramBlock'
import { createApolloClient } from '../../lib/createApolloClient'
import * as Sentry from '@sentry/nextjs'

// Dynamically imported (without ssr: false) so react-multi-carousel/
// react-slick code-split into their own chunks instead of shipping in
// this page's main bundle. These render real, SEO-relevant content, so
// SSR is kept -- don't add ssr: false here.
const ModelsBlock = dynamic(
  () => import('components/BlocksHomepage/ModelsBlock')
)
const CombinedModelsBlock = dynamic(
  () => import('components/BlocksHomepage/CombinedModelsBlock')
)
const BecomeAModel = dynamic(() => import('components/BecomeAModel'))
const OurWorldExtendedBlock = dynamic(
  () => import('components/BlocksHomepage/OurWorldExtendedBlock')
)

// Hoisted to module scope (rather than declared inside Index) so its
// component type is stable across renders. Defers mounting each section
// until it's near the viewport -- there's no native HTML equivalent for
// deferring an arbitrary subtree the way <img loading="lazy"> does for
// images, so this uses the same useInView-based approach as elsewhere in
// this codebase (e.g. hooks/useCarouselAutoplay.js).
function LazySection({ children }) {
  const { ref, inView } = useInView({ triggerOnce: true })
  return (
    <div ref={ref} style={inView ? undefined : { minHeight: 350 }}>
      {inView ? children : null}
    </div>
  )
}

LazySection.propTypes = {
  children: PropTypes.node,
}

// TODO: setup proptypes
function Index({
  globalSettings,
  sites,
  siteSlug,
  title = '',
  seo = {},
  site = '',
  acfHomepage = {},
  acfSeoAddress = {},
  instagrams = [],
  ourWorldLatestPosts = {},
  womenBoardUri = null,
  menBoardUri = null,
}) {
  const isSingleSite = typeof sites !== 'undefined' && sites.length === 1
  const [locationsSplash, setLoading] = useState(false)

  const globalUrl =
    process.env.NEXT_PUBLIC_SITE_URL || 'https://selectmodel.com'
  const siteUrl = `${globalUrl}/${site}`

  useEffect(() => {
    // Drives a timed splash/loading state with a cleanup timer, a
    // genuine side effect rather than a derivable value.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    const loadingTimer = setTimeout(() => setLoading(false), 1000 * 1.7)
    return () => clearTimeout(loadingTimer)
  }, [site])

  const siteName = useMemo(() => {
    if (site === 'modellink') return ''
    if (!sites) return ''

    const siteObj = sites.find((s) => s.path === `/${site}/`)

    return siteObj ? siteObj.name : ''
  }, [sites, site])

  if (locationsSplash) {
    return (
      <>
        <WordpressSEO url={`/${site}`} title={title} seo={seo} />
        <Loading>
          {!isSingleSite && locationsSplash && (
            <SplashText location={toTitleCase(site.replace('-', ' '))} />
          )}
        </Loading>
      </>
    )
  }

  const {
    highlightOrder,
    seoH1,
    exploreOurWorld,
    exploreOurWorldPosts,
    exploreOurWorldExtended,
    modelsInFocusTitle,
    modelsInFocus,
    modelsInFocusWomensTitle,
    modelsInFocusWomens,
    modelsInFocusMensTitle,
    modelsInFocusMens,
    promo,
  } = acfHomepage

  const highlightCovers = highlightOrder?.nodes ?? []
  const igHandle =
    globalSettings?.offices?.nodes?.[0]?.acfOffices?.instagramHandle || ''

  const logoName = site === 'modellink' ? 'modellink.svg' : 'selectmodel.svg'

  return (
    <StateInitializer
      globalSettings={globalSettings}
      sites={sites}
      siteSlug={siteSlug}
    >
      <Layout globalSettings={globalSettings}>
        <WordpressSEO url={`/${site}`} title={title} seo={seo} />
        <LocalBusinessJsonLd
          type="LocalBusiness"
          id={siteUrl}
          url={siteUrl}
          images={[`${globalUrl}/img/logo/${logoName}`]}
          name={title}
          description={null}
          telephone={acfSeoAddress.telephone}
          address={{
            streetAddress: acfSeoAddress?.streetAddress,
            addressLocality: acfSeoAddress?.addressLocality,
            addressRegion: acfSeoAddress?.addressRegion,
            postalCode: acfSeoAddress?.postalCode,
            addressCountry: acfSeoAddress?.addressCountry,
          }}
        />
        <SliderCover
          title={seoH1 !== null ? seoH1 : undefined}
          covers={highlightCovers}
        />
        <div className={s.container}>
          <LazySection>
            <OurWorldBlock
              siteName={siteName}
              posts={
                exploreOurWorld === 'latest-posts' && ourWorldLatestPosts?.nodes
                  ? ourWorldLatestPosts.nodes
                  : exploreOurWorldPosts?.nodes
              }
            />
          </LazySection>
          <LazySection>
            <OurWorldExtendedBlock posts={exploreOurWorldExtended} />
          </LazySection>
          <LazySection>
            <InstagramBlock handle={igHandle} posts={instagrams} />
          </LazySection>
          {modelsInFocus && modelsInFocus.length > 0 ? (
            <LazySection>
              <CombinedModelsBlock
                title={modelsInFocusTitle}
                models={modelsInFocus}
              />
            </LazySection>
          ) : (
            <LazySection>
              <ModelsBlock
                womensTitle={modelsInFocusWomensTitle}
                women={modelsInFocusWomens}
                womenBoardUri={womenBoardUri}
                mensTitle={modelsInFocusMensTitle}
                men={modelsInFocusMens}
                menBoardUri={menBoardUri}
              />
            </LazySection>
          )}
        </div>
        <LazySection>
          <BecomeAModel promo={promo} />
        </LazySection>
      </Layout>
    </StateInitializer>
  )
}

Index.propTypes = {
  globalSettings: PropTypes.object.isRequired,
  sites: PropTypes.array.isRequired,
  siteSlug: PropTypes.string.isRequired,
  pageUrl: PropTypes.string,
  pageTitle: PropTypes.string,
  pageDesc: PropTypes.string,
  acfSeoAddress: PropTypes.object,
  womenBoardUri: PropTypes.string,
  menBoardUri: PropTypes.string,
}

export default Index

export async function getStaticProps({ params }) {
  const { site } = params

  const apolloClient = createApolloClient()

  const [globalSettings, sites, { data, error }] = await Promise.all([
    getGlobalSettings(site),
    getSites(),
    apolloClient.query({
      query: INDEX_QUERY,
      context: { site },
      fetchPolicy: 'no-cache', // added because we were getting a stale response for '/chicago' route
    }),
  ])

  if (error) {
    Sentry.addBreadcrumb({
      category: 'getStaticProps',
      message: `Error loading Index props for site ${site}`,
      level: 'error',
      data: { params, error },
    })
    Sentry.captureException(new Error(`Error loading Index props - ${error}`))
    console.error(`Error loading Index props - ${error}`)
  }

  if (!data) return { props: {} }

  return {
    props: {
      ...params,
      ...data.pageBy,
      ...data.offices.nodes[0],
      instagrams: data.instagrams,
      ourWorldLatestPosts: data.ourWorldLatestPosts,
      womenBoardUri: data.womenBoard?.uri || null,
      menBoardUri: data.menBoard?.uri || null,
      globalSettings,
      sites,
      siteSlug: site,
    },
    revalidate: 60 * 60,
  }
}

export async function getStaticPaths() {
  const sites = await getSitesForStaticPaths()

  const paths = sites.map((site) => ({
    params: {
      site: site,
    },
  }))

  return {
    paths,
    fallback: false,
  }
}
